import React, { useState } from 'react';

export interface BookingData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  businessName: string;
  businessType: string;
  teamSize: string;
  currentSoftware: string;
  consultationType: string;
  specificChallenges: string;
  goals: string;
  budget: string;
  additionalInfo: string;
  date: string;
  time: string;
}

interface BookingFormProps {
  selectedDate: string;
  selectedTime: string;
  onBookingSubmit: (bookingData: BookingData) => void;
  onBack: () => void;
}

const BookingForm: React.FC<BookingFormProps> = ({
  selectedDate,
  selectedTime,
  onBookingSubmit,
  onBack
}) => {
  const [formData, setFormData] = useState<BookingData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    businessType: '',
    teamSize: '',
    currentSoftware: '',
    consultationType: '',
    specificChallenges: '',
    goals: '',
    budget: '',
    additionalInfo: '',
    date: selectedDate,
    time: selectedTime
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Partial<Record<keyof BookingData, string>>>({});

  const handleInputChange = (field: keyof BookingData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    setCurrentStep(prev => prev + 1);
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = () => {
    onBookingSubmit(formData);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Book Your Consultation</h2>
        <p className="text-sm text-gray-500">Step {currentStep} of 3</p>
      </div>

      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600">Selected Date & Time</p>
        <p className="font-semibold text-gray-800">
          {new Date(selectedDate).toLocaleDateString()} at {selectedTime}
        </p>
        <button onClick={onBack} className="text-red-600 hover:text-red-700 text-sm">
          Change
        </button>
      </div>

      {currentStep === 1 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold">Personal Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="First Name"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className="px-3 py-2 border rounded-lg"
            />
            <input
              type="text"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className="px-3 py-2 border rounded-lg"
            />
          </div>
          <input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
          />
          <input
            type="tel"
            placeholder="Phone"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      )}

      {currentStep === 2 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold">Business Information</h3>
          <input
            type="text"
            placeholder="Business Name"
            value={formData.businessName}
            onChange={(e) => handleInputChange('businessName', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
          />
          <select
            value={formData.businessType}
            onChange={(e) => handleInputChange('businessType', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="">Business Type</option>
            <option value="salon">Salon</option>
            <option value="spa">Spa</option>
            <option value="barbershop">Barbershop</option>
          </select>
        </div>
      )}

      {currentStep === 3 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold">Consultation Details</h3>
          <textarea
            placeholder="What challenges are you facing?"
            value={formData.specificChallenges}
            onChange={(e) => handleInputChange('specificChallenges', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            rows={4}
          />
          <textarea
            placeholder="What are your goals?"
            value={formData.goals}
            onChange={(e) => handleInputChange('goals', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            rows={3}
          />
        </div>
      )}

      <div className="flex justify-between mt-8">
        <button
          onClick={handlePrevious}
          disabled={currentStep === 1}
          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50"
        >
          Previous
        </button>

        {currentStep < 3 ? (
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-red-600 text-white rounded-lg"
          >
            Next
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-red-600 text-white rounded-lg"
          >
            Submit Booking
          </button>
        )}
      </div>
    </div>
  );
};

export default BookingForm;